import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { toast } from '@/components/feedback/useToast';

import { roleApi } from '../api/role.adapter';
import { useCan, useHeldRoleNames } from '../hooks/use-can';
import { useDeleteRole } from '../hooks/use-role-mutations';
import { roleQueryKeys } from '../hooks/use-roles';
import { useRoleDetailStore } from '../store/role-detail.store';
import { type Role } from '../types/role.types';
import { isPermissionDenied } from '@/api/permission-error';

import { getRoleErrorMessage } from '../utils/error-messages';
import { formatDateTime } from '../utils/format-date';
import { DeleteRoleConfirmModal } from './DeleteRoleConfirmModal';
import { SeedEditWarningModal } from './SeedEditWarningModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

interface RoleListRowProps {
  role: Role;
}

export function RoleListRow({ role }: RoleListRowProps) {
  const { t } = useTranslation('roles');
  const openView = useRoleDetailStore((s) => s.openView);
  const openEdit = useRoleDetailStore((s) => s.openEdit);
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteRole();
  const heldRoleNames = useHeldRoleNames();
  const { canWrite, denyProps, tooltip } = useWriteGuard('SYSTEM_MGMT');

  const [showSeedWarning, setShowSeedWarning] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [impactCount, setImpactCount] = useState(0);
  const [isFetchingImpact, setIsFetchingImpact] = useState(false);
  const [errorPopup, setErrorPopup] = useState<string | null>(null);

  // SHCT-582 — PRD 21 §7.4 P-4 fixes the channel for a server RBAC refusal at 토스트(error),
  // 화면 유지. This screen reports its own business rules (§8.2 — 삭제 실패, 정보 조회 실패) in a
  // 팝업, which is right for those and wrong for this one: a permission refusal is not a decision
  // the operator has to acknowledge. Route that single code to the toast and leave §8.2 intact.
  const reportError = (e: unknown, fallbackKey: 'errors.loadInfo' | 'errors.deleteFailed') => {
    const message = getRoleErrorMessage(e, t, fallbackKey);
    if (isPermissionDenied(e)) toast.error(message);
    else setErrorPopup(message);
  };

  const isRoot = role.isReserved;
  const isSeed = role.isSeed;
  const isHeldBySelf = !isRoot && !isSeed && heldRoleNames.includes(role.name);

  const canEdit = !isRoot && canWrite;
  const canDelete = !isRoot && !isSeed && !isHeldBySelf && canWrite;

  const handleEdit = () => {
    if (isSeed) {
      setShowSeedWarning(true);
    } else {
      openEdit(role);
    }
  };

  const confirmSeedEdit = () => {
    setShowSeedWarning(false);
    openEdit(role);
  };

  const handleDeleteClick = async () => {
    setIsFetchingImpact(true);
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: roleQueryKeys.detail(role.id),
        queryFn: () => roleApi.getRole(role.id),
      });
      setImpactCount(fresh.adminCount);
      setShowDelete(true);
    } catch (e) {
      // SHCT-631 — this is the impact-count prefetch (GET /roles/{id}), not the delete itself, so
      // PRD §8.2's load copy applies. It used to fall back to the generic "알 수 없는 오류",
      // which is the same complaint QC filed against the delete path.
      reportError(e, 'errors.loadInfo');
    } finally {
      setIsFetchingImpact(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const result = await deleteMutation.mutateAsync(role.id);
      const suffix =
        result.affectedCount > 0
          ? t('toasts.deletedSuffix', { count: result.affectedCount })
          : '';
      toast.success(t('toasts.deleted', { name: role.name }) + suffix);
      setShowDelete(false);
    } catch (e) {
      // PRD §8.2: delete failure surfaces a dedicated popup; the role stays in the list.
      setShowDelete(false);
      reportError(e, 'errors.deleteFailed');
    }
  };

  const editTooltip = isRoot
    ? t('tooltips.rootEditBlocked')
    : !canWrite
    ? t('tooltips.noWritePermission')
    : undefined;

  const deleteTooltip = isRoot
    ? t('tooltips.rootDeleteBlocked')
    : isSeed
    ? t('tooltips.seedDeleteBlocked')
    : isHeldBySelf
    ? t('tooltips.selfHeldDeleteBlocked')
    : !canWrite
    ? t('tooltips.noWritePermission')
    : undefined;

  return (
    <>
      <tr>
        <td>{role.id}</td>
        <td>
          <div style={{ display: 'inline-flex', gap: 6 }}>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={() => openView(role)}
            >
              {t('actions.view')}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline role-action-btn"
              style={{ padding: '4px 10px', fontSize: 12 }}
              disabled={!canEdit}
              // SHCT-582 — PRD 21 §7.4 P-3 marker, and only when the PERMISSION is the blocker:
              // the Root row and a seed Role are refused for reasons a grant would not change,
              // and their own tooltips say so.
              {...(canWrite ? {} : denyProps)}
              title={editTooltip}
              onClick={handleEdit}
            >
              {t('actions.modify')}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline role-action-btn"
              style={{
                padding: '4px 10px',
                fontSize: 12,
                color: canDelete ? 'var(--destructive)' : undefined,
              }}
              disabled={!canDelete || isFetchingImpact}
              {...(canWrite ? {} : denyProps)}
              title={deleteTooltip}
              onClick={handleDeleteClick}
            >
              {isFetchingImpact ? t('actions.checking') : t('actions.delete')}
            </button>
          </div>
        </td>
        <td>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 500 }}>{role.name}</span>
            {isRoot && (
              <span
                className="badge badge-destructive"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Crown size={12} />
                {t('badges.root')}
              </span>
            )}
            {isSeed && <span className="badge badge-secondary">{t('badges.seed')}</span>}
          </div>
        </td>
        <td>{formatDateTime(role.updatedAt)}</td>
        <td>{formatDateTime(role.createdAt)}</td>
      </tr>
      <SeedEditWarningModal
        open={showSeedWarning}
        roleName={role.name}
        onConfirm={confirmSeedEdit}
        onCancel={() => setShowSeedWarning(false)}
      />
      <DeleteRoleConfirmModal
        open={showDelete}
        roleName={role.name}
        impactCount={impactCount}
        isDeleting={deleteMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setShowDelete(false)}
      />
      <InfoModal
        open={errorPopup !== null}
        title={t('modals.error.title')}
        body={errorPopup ?? ''}
        confirmLabel={t('actions.confirm')}
        onClose={() => setErrorPopup(null)}
      />
    </>
  );
}
